import { memo, useState } from "react";
import { Check, Copy } from "lucide-react";
import { Highlight } from "prism-react-renderer";
import { Button } from "~/components/ui/button";
import usePrismLanguages from "~/hooks/usePrismLanguages";
import { useCodeTheme } from "~/lib/codeTheme";
import { cn } from "~/lib/utils";

interface HighlightedCodeProps {
  code: string;
  language: string;
  // Tailwind classes for the <pre> (background, padding, radius, overflow…).
  className?: string;
  // Tailwind classes for the positioned wrapper (e.g. block margins).
  wrapperClassName?: string;
  // Paint the <pre> with the Prism theme's own background instead of leaving
  // the background to `className`.
  useThemeBackground?: boolean;
  // Reveal the copy button only on hover/focus rather than always showing it.
  copyOnHoverOnly?: boolean;
}

// Prism-highlighted code with a copy button. Owns the clipboard handling and
// the token-render loop; callers supply the surrounding chrome via className.
export const HighlightedCode = memo(function HighlightedCode({
  code,
  language,
  className,
  wrapperClassName,
  useThemeBackground = false,
  copyOnHoverOnly = false,
}: HighlightedCodeProps) {
  const { Prism } = usePrismLanguages();
  const codeTheme = useCodeTheme();
  const [copied, setCopied] = useState(false);
  const trimmed = code.trimEnd();

  const onCopy = async () => {
    await navigator.clipboard.writeText(trimmed);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className={cn("group relative", wrapperClassName)}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onCopy}
        className={cn(
          "absolute right-1 top-1 h-7 px-2 text-xs text-muted-foreground",
          copyOnHoverOnly &&
            "opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
        )}
        aria-label={copied ? "Copied" : "Copy code"}
      >
        {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      </Button>
      <Highlight theme={codeTheme} code={trimmed} language={language || "text"} prism={Prism}>
        {({ style, tokens, getLineProps, getTokenProps }) => (
          <pre
            className={cn("font-mono", className)}
            style={useThemeBackground ? style : undefined}
          >
            {tokens.map((line, i) => (
              <div key={i} {...getLineProps({ line })}>
                {line.map((token, j) => (
                  <span key={j} {...getTokenProps({ token })} />
                ))}
              </div>
            ))}
          </pre>
        )}
      </Highlight>
    </div>
  );
});
